import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxSpinner, NgxSpinnerService, NgxSpinnerComponent } from 'ngx-spinner';
import { Observable, Subject, firstValueFrom, takeUntil } from 'rxjs';
import { SORT_OPTIONS } from '../../_shared/_constants/sort-options';
import { IProductType, IShopifyProduct, IVendor, SEO } from '@app/_shared/_models';
import { CollectionService } from '../../_shared/_services/collection.service';
import { PrismicService } from '../../_shared/_services/prismic.service';
import { ProductTypeService } from '../../_shared/_services/product-type.service';
import { ProductService } from '../../_shared/_services/product.service';
import { SeoService } from '../../_shared/_services/seo.service';
import { VendorService } from '../../_shared/_services/vendor.service';
import { IBrandPage } from '../_prismic/brand-page';
import { ImageComponent } from '../../_shared/_components/image/image.component';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { ProductCardComponent } from '../../_shared/_components/product-card/product-card.component';
import { ShopByBrandComponent } from '../../_shared/_components/shop-by-brand/shop-by-brand.component';
import { RouterLink } from '@angular/router';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-freshwater',
    templateUrl: './freshwater.component.html',
    styleUrls: ['./freshwater.component.scss'],
    imports: [ImageComponent, MatFormField, MatSelect, FormsModule, ReactiveFormsModule, MatOption, NgxSpinnerComponent, ProductCardComponent, ShopByBrandComponent, RouterLink, AsyncPipe]
})
export class FreshwaterComponent implements OnInit, OnDestroy {
  private readonly prismicService = inject(PrismicService);
  private readonly seoService = inject(SeoService);
  private readonly vendorService = inject(VendorService);
  private readonly productService = inject(ProductService);
  private readonly ngxSpinnerService = inject(NgxSpinnerService);
  private readonly collectionService = inject(CollectionService);
  private readonly productTypeService = inject(ProductTypeService);

  public seo: SEO = {
    image: 'https://mypoolhut.com/assets/images/logos/Freshwater-Salt-System-Logo.png',
    metaDescription: 'Shop all Freshwater spa and hot tub products at My Pool Hut. Free shipping on all orders over $60!',
    metaTitle: 'Freshwater'
  };
  public spinner$!: Observable<NgxSpinner>;
  public brandPage!: IBrandPage;
  public sortOptions = SORT_OPTIONS;
  public sortControl = new UntypedFormControl();
  public categories = new Array<IProductType>();
  public categoriesControl = new UntypedFormControl();
  public vendors = new Array<IVendor>();
  public products$!: Observable<Array<IShopifyProduct>>;
  public filteredProducts = new Array<IShopifyProduct>();
  public selectedSortOption = this.sortOptions[0];

  private readonly unsubscribe: Subject<undefined> = new Subject<undefined>();

  constructor() {
    this.products$ = this.collectionService.products$;
  }

  public async ngOnInit(): Promise<void> {
    this.spinner$ = this.ngxSpinnerService.spinnerObservable;
    this.seoService.setMetaData(this.seo);
    this.seoService.createCanonicalUrl('https://mypoolhut.com/brands/freshwater');
    this.selectedSortOption = this.sortOptions[0];
    this.sortControl.setValue(this.selectedSortOption);

    const res = await firstValueFrom(this.prismicService.getByTag('freshwater_brand_page'));
    if (res && res.results && res.results.length > 0) {
      const response = res.results[0].data;
      this.brandPage = {
        hero_image: response.hero_image,
        hero_title: response.hero_title,
        subsection_title: response.subsection_title,
        hero_body: this.prismicService.getRichText(response.hero_body),
        brandsReel: this.prismicService.getBrandsFromSlice(response.body)
      }
    }

    this.categories = this.productTypeService.productTypes;
    this.collectionService.loadProducts('hot-tub-care-supplies');

    const vendor = this.vendorService.vendors.find(v => v.name === 'FreshWater');
    if (vendor) {
      this.vendors.push(vendor);
    }

    this.products$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(async (products: Array<IShopifyProduct>) => {
        if (products?.length > 0) {
          this.filteredProducts = ProductService.filterByVendors(products, this.vendors);
          this.filteredProducts = ProductService.sortProducts(this.filteredProducts, this.sortControl.value);

          this.categoriesControl.valueChanges
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((categories: Array<IProductType>) => {
              if (categories?.length < 1) {
                this.filteredProducts = ProductService.filterByVendors(products, this.vendors);
              } else {
                this.filteredProducts = ProductService.filterByVendors(products, this.vendors);
                this.filteredProducts = ProductService.filterByProductType(this.filteredProducts, categories);
              }
              this.filteredProducts = ProductService.sortProducts(this.filteredProducts, this.sortControl.value);
            });
        }
      });

    this.sortControl.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((sortOption: string) => {
        this.filteredProducts = ProductService.sortProducts(this.filteredProducts, sortOption);
      });
  }

  public ngOnDestroy(): void {
    this.seoService.removeCanonicalUrl();
    this.unsubscribe.next(undefined);
    this.unsubscribe.complete();
  }

  public compareCategories(object1: IProductType, object2: IProductType): boolean {
    return object1 && object2 && object1.label == object2.label;
  }

  public clearFilters(): void {
    this.categoriesControl.setValue([]);
  }
}
